/**
 * Dual Symbol Dashboard — side-by-side BANKNIFTY / NIFTY comparison view,
 * matching the reference "OI Dashboard 1.pdf" dual-column layout.
 *
 * Added as a NEW tab alongside the existing single-symbol Option Indicator
 * page per explicit decision (toggle/tab, not a replacement) — the
 * single-symbol page with its dropdown switcher is left untouched.
 *
 * Self-contained: fetches both symbols' dashboard/alerts/composite-score
 * in parallel via the existing per-symbol optionIndicatorService (zero new
 * backend endpoints), and renders a condensed two-column comparison
 * covering the same sections as the single-symbol page — OI Composite
 * Score V2, OI Intelligence signals (Flow by Timeframe, Sustained
 * Strikes, OI Concentration, Strike Velocity, OI/Price Divergence, New
 * Strike Activation, Roll Detection), ATM CE/PE Ratio + Straddle, Zone
 * Skew, GEX, Volume PCR, Historical Percentiles, and Latest Alerts.
 *
 * Deliberately more compact per-field than the single-symbol page since
 * two columns share the screen width; small helpers below are locally
 * duplicated (not imported) from OptionIndicatorPage.tsx to avoid any
 * risk of touching that file.
 */
import { indexSectionProps, useAnalystIndexView } from "@/context/AnalystIndexViewContext";
import { useCallback, useEffect, useState } from "react";
import type { ReactNode } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import optionIndicatorService from "@/services/optionIndicatorService";
import type {
  OIScoreSnapshot,
  OptionIndicatorAlertsRead,
  OptionIndicatorDashboardRead,
} from "@/types";

const MONO_FONT = '"Roboto Mono", "SF Mono", Consolas, monospace';
const SYMBOLS = ["BANKNIFTY", "NIFTY"] as const;
type Symbol = (typeof SYMBOLS)[number];

function n(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}
function fmt(value: string | number | null | undefined, digits = 2): string {
  const parsed = n(value);
  return parsed === null ? "—" : parsed.toFixed(digits);
}
function fmtInt(value: number | null | undefined): string {
  return value === null || value === undefined ? "—" : value.toLocaleString("en-IN");
}
function directionColor(direction?: string | null): "success" | "error" | "default" {
  if (direction === "bull") return "success";
  if (direction === "bear") return "error";
  return "default";
}

interface SymbolData {
  dashboard: OptionIndicatorDashboardRead | null;
  alerts: OptionIndicatorAlertsRead | null;
  compositeScore: OIScoreSnapshot | null;
  error: string | null;
  loading: boolean;
}

function emptySymbolData(): SymbolData {
  return { dashboard: null, alerts: null, compositeScore: null, error: null, loading: true };
}

/** One label:value line — the PDF's own dense text-list convention. */
function InfoLine({ label, value, color }: { label: string; value: ReactNode; color?: string }) {
  return (
    <Typography variant="caption" display="block" sx={{ fontFamily: MONO_FONT }} color={color}>
      <Box component="span" color="text.secondary">
        {label}:{" "}
      </Box>
      {value}
    </Typography>
  );
}

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <Typography
      variant="caption"
      fontWeight={700}
      display="block"
      sx={{ mt: 1.25, mb: 0.25, textTransform: "uppercase" }}
    >
      {children}
    </Typography>
  );
}

function SymbolColumn({ symbol, data }: { symbol: Symbol; data: SymbolData }) {
  const { dashboard, alerts, compositeScore, error, loading } = data;

  if (loading && !dashboard) {
    return (
      <Paper variant="outlined" sx={{ p: 2, textAlign: "center" }}>
        <CircularProgress size={20} />
      </Paper>
    );
  }
  if (error && !dashboard) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          {symbol}
        </Typography>
        <Alert severity="warning">{error}</Alert>
      </Paper>
    );
  }
  if (!dashboard) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          {symbol}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No scored data yet.
        </Typography>
      </Paper>
    );
  }

  const oi = dashboard.oi_intelligence;
  const sc = dashboard.strike_conviction;

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      {/* ---- Ticker header ---- */}
      <Stack direction="row" alignItems="baseline" justifyContent="space-between" flexWrap="wrap">
        <Typography variant="subtitle1" fontWeight={700}>
          {symbol}
        </Typography>
        <Typography variant="body2" sx={{ fontFamily: MONO_FONT }}>
          {dashboard.spot.toLocaleString("en-IN")}
        </Typography>
      </Stack>
      {compositeScore && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }} flexWrap="wrap">
          <Chip
            size="small"
            label={`${compositeScore.regime_name} [${compositeScore.position_size}]`}
            color={directionColor(compositeScore.direction)}
          />
          <Typography variant="caption" color="text.secondary">
            PCR {fmt(compositeScore.local_pcr, 3)} · {compositeScore.decision}
          </Typography>
        </Stack>
      )}
      <Divider sx={{ my: 1 }} />

      {/* ---- Composite Score V2 ---- */}
      {compositeScore && (
        <>
          <Stack direction="row" alignItems="baseline" spacing={1}>
            <Typography variant="h5" fontWeight={700} sx={{ fontFamily: MONO_FONT }}>
              {fmt(compositeScore.score_v2, 2)}
              <Typography component="span" variant="caption" color="text.secondary">
                /10
              </Typography>
            </Typography>
            <Chip
              size="small"
              label={compositeScore.regime}
              color={directionColor(compositeScore.direction)}
              variant="outlined"
            />
          </Stack>
          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
            {compositeScore.regime_note}
          </Typography>
          <Grid container spacing={0.5}>
            {(["C1", "C2", "C3", "C4", "C5", "C6"] as const).map((label) => (
              <Grid item xs={4} key={label}>
                <Typography variant="caption" sx={{ fontFamily: MONO_FONT }} display="block">
                  {label}: {fmt(compositeScore.components[label])}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {/* ---- OI Flow by Timeframe / Sustained Strikes ---- */}
      {sc && (
        <>
          <SectionLabel>Flow by timeframe (5/10/15min)</SectionLabel>
          <InfoLine
            label="Call L"
            value={`${sc.net_call_flow_5min} / ${sc.net_call_flow_10min} / ${sc.net_call_flow_15min}`}
          />
          <InfoLine
            label="Put L"
            value={`${sc.net_put_flow_5min} / ${sc.net_put_flow_10min} / ${sc.net_put_flow_15min}`}
          />
          <InfoLine
            label="Fresh PCR"
            value={`${sc.pcr_5min ?? "—"} / ${sc.pcr_10min ?? "—"} / ${sc.pcr_15min ?? "—"}`}
          />

          <SectionLabel>
            Sustained strikes ({sc.sustained_calls.length + sc.sustained_puts.length})
          </SectionLabel>
          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
            {sc.conviction_summary}
          </Typography>
          {sc.sustained_calls.slice(0, 2).map((s) => (
            <InfoLine key={`c-${s.strike}`} label={`${s.strike}CE`} value={`${s.trend} +${s.chg_L}L`} color="error.main" />
          ))}
          {sc.sustained_puts.slice(0, 2).map((s) => (
            <InfoLine key={`p-${s.strike}`} label={`${s.strike}PE`} value={`${s.trend} +${s.chg_L}L`} color="success.main" />
          ))}
        </>
      )}

      {/* ---- OI Intelligence signals ---- */}
      {oi && (
        <>
          <SectionLabel>OI concentration</SectionLabel>
          <InfoLine
            label="Call"
            value={`${oi.oi_concentration.call_concentration_pct.toFixed(1)}% — ${oi.oi_concentration.call_conc_label}`}
          />
          <InfoLine
            label="Put"
            value={`${oi.oi_concentration.put_concentration_pct.toFixed(1)}% — ${oi.oi_concentration.put_conc_label}`}
          />

          <SectionLabel>Strike velocity</SectionLabel>
          {oi.strike_velocity ? (
            <>
              <InfoLine
                label="CE"
                value={`${oi.strike_velocity.sv_call_strike} +${oi.strike_velocity.sv_call_chg.toLocaleString("en-IN")}`}
              />
              <InfoLine
                label="PE"
                value={`${oi.strike_velocity.sv_put_strike} +${oi.strike_velocity.sv_put_chg.toLocaleString("en-IN")}`}
              />
            </>
          ) : (
            <Typography variant="caption" color="text.secondary" display="block">
              Not enough data.
            </Typography>
          )}

          <SectionLabel>OI vs price divergence</SectionLabel>
          <Typography variant="caption" display="block">
            {oi.oi_divergence.divergence_label}
          </Typography>

          <SectionLabel>New strike activation</SectionLabel>
          <Typography variant="caption" color="text.secondary" display="block">
            {oi.new_strike_activation.label}
          </Typography>

          <SectionLabel>Roll detection</SectionLabel>
          <Typography variant="caption" color="text.secondary" display="block">
            {oi.roll_detection.roll_label}
          </Typography>
        </>
      )}

      {/* ---- ATM CE/PE + Straddle ---- */}
      {dashboard.atm_ce_pe_ratio && (
        <>
          <SectionLabel>ATM CE/PE ratio &amp; straddle</SectionLabel>
          <InfoLine
            label="Ratio"
            value={`${dashboard.atm_ce_pe_ratio.atm_strike} — ${fmt(dashboard.atm_ce_pe_ratio.ce_pe_ratio, 3)}`}
          />
          <InfoLine
            label="Straddle"
            value={`${fmtInt(dashboard.atm_ce_pe_ratio.straddle_oi)} (${dashboard.atm_ce_pe_ratio.straddle_status})`}
          />
        </>
      )}

      {/* ---- Zone Skew ---- */}
      <SectionLabel>Zone skew (fresh PCR by distance)</SectionLabel>
      <InfoLine
        label="Near/Mid/Far"
        value={`${fmt(dashboard.zone_skew?.near?.fresh_pcr_local, 3)} / ${fmt(
          dashboard.zone_skew?.mid?.fresh_pcr_local,
          3
        )} / ${fmt(dashboard.zone_skew?.far?.fresh_pcr_local, 3)}`}
      />
      {dashboard.zone_divergence && (
        <Typography variant="caption" color="text.secondary" display="block">
          {dashboard.zone_divergence}
        </Typography>
      )}

      {/* ---- GEX ---- */}
      <SectionLabel>GEX (gamma exposure)</SectionLabel>
      <InfoLine label="Total" value={`${fmt(dashboard.gex.gex_total_cr, 0)}Cr — ${dashboard.gex.gex_label}`} />
      <InfoLine label="Flip level" value={fmtInt(dashboard.gex.gex_flip_level)} />

      {/* ---- Volume PCR ---- */}
      <SectionLabel>Volume PCR</SectionLabel>
      <InfoLine
        label="Ratio"
        value={`${fmt(dashboard.volume_pcr.vol_pcr_local, 3)} — ${dashboard.volume_pcr.pressure_label}`}
      />

      {/* ---- Historical Percentiles ---- */}
      <SectionLabel>Historical percentiles (last {dashboard.percentiles.n_sessions_local_pcr} sessions)</SectionLabel>
      <InfoLine label="Local PCR" value={dashboard.percentiles.local_pcr_label} />
      <InfoLine label="ATM IV" value={dashboard.percentiles.atm_iv_label} />
      <InfoLine label="GEX" value={dashboard.percentiles.gex_total_cr_label} />

      {/* ---- Latest Alerts ---- */}
      <SectionLabel>Latest alerts</SectionLabel>
      {alerts && alerts.events.length > 0 ? (
        alerts.events.slice(0, 5).map((event, i) => (
          <Stack key={i} direction="row" spacing={0.5} alignItems="baseline" sx={{ mb: 0.25 }}>
            {event.priority && (
              <Chip
                size="small"
                label={event.priority}
                color={event.priority === "P1" ? "error" : "warning"}
                sx={{ height: 16, fontSize: "0.6rem" }}
              />
            )}
            <Typography variant="caption" color="text.secondary">
              {event.description}
            </Typography>
          </Stack>
        ))
      ) : (
        <Typography variant="caption" color="text.secondary">
          No alerts yet.
        </Typography>
      )}
    </Paper>
  );
}

export function DualSymbolDashboardPage() {
  const view = useAnalystIndexView();
  const [data, setData] = useState<Record<Symbol, SymbolData>>({
    BANKNIFTY: emptySymbolData(),
    NIFTY: emptySymbolData(),
  });
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);

  const fetchAll = useCallback(async () => {
    await Promise.all(
      SYMBOLS.map(async (symbol) => {
        try {
          const [dashboard, alerts, compositeScore] = await Promise.all([
            optionIndicatorService.getDashboard(symbol),
            optionIndicatorService.getAlerts(symbol, 10).catch(() => null),
            optionIndicatorService.getCompositeScore(symbol).catch(() => null),
          ]);
          setData((prev) => ({
            ...prev,
            [symbol]: { dashboard, alerts, compositeScore, error: null, loading: false },
          }));
        } catch (err) {
          const message = err instanceof Error ? err.message : "Failed to load dashboard.";
          setData((prev) => ({
            ...prev,
            [symbol]: { ...prev[symbol], error: message, loading: false },
          }));
        }
      })
    );
    setLastRefreshedAt(new Date());
  }, []);

  useEffect(() => {
    fetchAll();
    const interval = setInterval(fetchAll, 60000);
    return () => clearInterval(interval);
  }, [fetchAll]);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }} flexWrap="wrap">
        <Typography variant="body2" color="text.secondary">
          BANKNIFTY and NIFTY side by side — same data as the single-symbol Option Indicator tab.
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          {lastRefreshedAt && (
            <Typography variant="caption" color="text.secondary">
              Last refreshed {lastRefreshedAt.toLocaleTimeString("en-IN", { hour12: false })}
            </Typography>
          )}
          <Button size="small" variant="outlined" onClick={() => fetchAll()}>
            Refresh
          </Button>
        </Stack>
      </Stack>
      <Grid container spacing={1.5}>
        <Grid item xs={12} {...indexSectionProps(view, "BANKNIFTY", "md")}>
          <SymbolColumn symbol="BANKNIFTY" data={data.BANKNIFTY} />
        </Grid>
        <Grid item xs={12} {...indexSectionProps(view, "NIFTY", "md")}>
          <SymbolColumn symbol="NIFTY" data={data.NIFTY} />
        </Grid>
      </Grid>
    </Box>
  );
}

export default DualSymbolDashboardPage;
